import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  query,
  collection,
  where,
  getDocs,
  limit,
  orderBy,
  increment,
  Timestamp,
} from "firebase/firestore";
import { getDB, getFirebaseAuth } from "../Firebase";

// ── Получаем конфиг платформы (startingCoins, defaultRating) ─────────────────
export interface PlatformConfig {
  startingCoins: number;
  defaultRating: number;
  betOptions: number[];
  maxBet: number;
}

const CONFIG_CACHE: { data: PlatformConfig | null; fetchedAt: number } = {
  data: null,
  fetchedAt: 0,
};
const CONFIG_TTL_MS = 5 * 60 * 1000; // кешируем на 5 минут

export async function getPlatformConfig(): Promise<PlatformConfig> {
  const now = Date.now();
  if (CONFIG_CACHE.data && now - CONFIG_CACHE.fetchedAt < CONFIG_TTL_MS) {
    return CONFIG_CACHE.data;
  }
  try {
    const snap = await getDoc(doc(getDB(), "settings", "global"));
    const data = snap.exists() ? snap.data() : {};
    const cfg: PlatformConfig = {
      startingCoins: typeof data.startingCoins === "number" ? data.startingCoins : 0,
      defaultRating:  typeof data.defaultRating  === "number" ? data.defaultRating  : 500,
      betOptions: Array.isArray(data.betOptions) ? data.betOptions : [50, 100, 250, 500, 1000],
      maxBet: typeof data.maxBet === "number" ? data.maxBet : 1000,
    };
    CONFIG_CACHE.data = cfg;
    CONFIG_CACHE.fetchedAt = now;
    return cfg;
  } catch {
    return { startingCoins: 0, defaultRating: 500, betOptions: [50, 100, 250, 500, 1000], maxBet: 1000 };
  }
}

export interface UserProfile {
  uid: string;
  nickname: string;
  rating: number;
  level: number;
  experience: number;
  coins: number;
  wins: number;
  losses: number;
  gammonsWon: number;
  backgammonsWon: number;
  winStreak: number;
  longestWinStreak: number;
  gamesPlayed: number;
  achievements: string[];
  createdAt: Timestamp | null;
  lastSeen: Timestamp | null;
  banned?: boolean;
  banReason?: string;
  adminNote?: string;
  startingBalanceGranted?: boolean;
}

export const USERS_COLLECTION = "users";
/** Значения по умолчанию используются как fallback если settings/global недоступен */
export const DEFAULT_RATING = 500;
export const STARTING_COINS = 0;
const LOCAL_PROFILE_KEY = "bg_profile_v2";

// ── localStorage helpers ──────────────────────────────────────────────────────

function saveLocal(profile: UserProfile) {
  try {
    localStorage.setItem(LOCAL_PROFILE_KEY, JSON.stringify(profile));
  } catch (_) {}
}

function loadLocal(uid?: string): UserProfile | null {
  try {
    const raw = localStorage.getItem(LOCAL_PROFILE_KEY);
    if (!raw) return null;
    const p: UserProfile = JSON.parse(raw);
    if (uid && p.uid !== uid) return null;
    return p;
  } catch (_) {
    return null;
  }
}

// ─────────────────────────────────────────────────────────────────────────────

export function getDefaultProfile(
  uid: string,
  nickname: string,
  startingCoins = STARTING_COINS,
  defaultRating = DEFAULT_RATING,
): UserProfile {
  return {
    uid,
    nickname,
    rating: defaultRating,
    level: 1,
    experience: 0,
    coins: startingCoins,
    wins: 0,
    losses: 0,
    gammonsWon: 0,
    backgammonsWon: 0,
    winStreak: 0,
    longestWinStreak: 0,
    gamesPlayed: 0,
    achievements: [],
    createdAt: null,
    lastSeen: null,
  };
}

export async function getUserProfile(
  uid: string
): Promise<UserProfile | null> {
  // 1. Try Firestore
  let profile: UserProfile | null = null;
  try {
    const db = getDB();
    const snap = await getDoc(doc(db, USERS_COLLECTION, uid));
    if (snap.exists()) {
      profile = snap.data() as UserProfile;
      saveLocal(profile);
    }
  } catch (err) {
    console.warn("Firestore unavailable, falling back to localStorage", err);
    profile = loadLocal(uid);
  }

  // 2. Если профиль есть но coins=0 — пробуем починить через сервер
  if (profile && (profile.coins ?? 0) === 0) {
    try {
      const auth = getFirebaseAuth();
      const user = auth.currentUser;
      const token = user ? await user.getIdToken() : null;
      if (token) {
        const resp = await fetch("/api/user/fix-balance", {
          method: "POST",
          headers: { "Authorization": `Bearer ${token}` },
        });
        if (resp.ok) {
          const data = await resp.json();
          if (data.fixed && data.profile) {
            profile = data.profile as UserProfile;
            saveLocal(profile);
            console.log("[getUserProfile] Баланс исправлен:", profile.coins);
          }
        }
      }
    } catch (err) {
      console.warn("[getUserProfile] fix-balance failed:", err);
    }
  }

  return profile ?? loadLocal(uid);
}

export async function createUserProfile(
  uid: string,
  nickname: string
): Promise<UserProfile> {
  // Получаем токен через правильный экземпляр Firebase Auth
  let token: string | null = null;
  try {
    const auth = getFirebaseAuth();
    let user = auth.currentUser;
    // Если currentUser ещё не загружен — ждём onAuthStateChanged
    if (!user) {
      user = await new Promise((resolve) => {
        const unsub = auth.onAuthStateChanged((u) => { unsub(); resolve(u); });
      });
    }
    token = user ? await user.getIdToken() : null;
  } catch (err) {
    console.error("[createUserProfile] Не удалось получить токен:", err);
  }

  // Создаём профиль через сервер — он читает startingCoins из settings/global
  if (token) {
    try {
      const resp = await fetch("/api/user/init", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({ nickname }),
      });
      if (resp.ok) {
        const data = await resp.json();
        const profile = data.profile as UserProfile;
        saveLocal(profile);
        console.log("[createUserProfile] Профиль создан через сервер, coins:", profile.coins);
        return profile;
      } else {
        const errBody = await resp.text().catch(() => "(no body)");
        console.error("[createUserProfile] Сервер вернул", resp.status, errBody);
      }
    } catch (err) {
      console.error("[createUserProfile] fetch /api/user/init упал:", err);
    }
  } else {
    console.error("[createUserProfile] Токен не получен — fallback на клиентский путь");
  }

  // Fallback: создаём локально с настройками из Firestore (или 0 если недоступен)
  console.warn("[createUserProfile] Используется fallback (клиентский путь)");
  const cfg = await getPlatformConfig();
  const profile = getDefaultProfile(uid, nickname, cfg.startingCoins, cfg.defaultRating);
  saveLocal(profile);

  try {
    const db = getDB();
    await setDoc(doc(db, USERS_COLLECTION, uid), {
      ...profile,
      createdAt: serverTimestamp(),
      lastSeen: serverTimestamp(),
    });
  } catch (err) {
    console.warn("Could not sync profile to Firestore:", err);
  }

  return profile;
}

export async function updateLastSeen(uid: string): Promise<void> {
  try {
    await updateDoc(doc(getDB(), USERS_COLLECTION, uid), {
      lastSeen: serverTimestamp(),
    });
  } catch (_) {}
}

export async function isNicknameAvailable(nickname: string): Promise<boolean> {
  try {
    const q = query(
      collection(getDB(), USERS_COLLECTION),
      where("nickname", "==", nickname),
      limit(1)
    );
    const snap = await getDocs(q);
    return snap.empty;
  } catch (_) {
    // If Firestore is unavailable, allow the name (best-effort)
    return true;
  }
}

export interface GameResultUpdate {
  isWinner: boolean;
  isGammon: boolean;
  isBackgammon: boolean;
  ratingDelta: number;
  expGained: number;
  coinsGained: number;
  newAchievements?: string[];
}

export async function applyGameResult(
  uid: string,
  result: GameResultUpdate,
  options: { skipCoins?: boolean } = {}
): Promise<void> {
  const skipCoins = !!options.skipCoins;

  // Update localStorage immediately
  const local = loadLocal(uid);
  if (local) {
    const newWinStreak = result.isWinner ? local.winStreak + 1 : 0;
    const newExp = local.experience + result.expGained;
    const { newLevel, remainingExp } = calculateLevel(local.level, newExp);

    const updated: UserProfile = {
      ...local,
      rating: Math.max(100, local.rating + result.ratingDelta),
      gamesPlayed: local.gamesPlayed + 1,
      experience: remainingExp,
      level: newLevel,
      // В сетевой игре монеты выставляет сервер (/api/match/settle). Клиент не
      // прибавляет coinsGained, иначе получится двойное начисление.
      coins: skipCoins ? local.coins : local.coins + result.coinsGained,
      winStreak: newWinStreak,
      longestWinStreak: Math.max(local.longestWinStreak, newWinStreak),
      wins: result.isWinner ? local.wins + 1 : local.wins,
      losses: result.isWinner ? local.losses : local.losses + 1,
      gammonsWon:
        result.isWinner && result.isGammon
          ? local.gammonsWon + 1
          : local.gammonsWon,
      backgammonsWon:
        result.isWinner && result.isBackgammon
          ? local.backgammonsWon + 1
          : local.backgammonsWon,
    };
    saveLocal(updated);
  }

  // Try Firestore (non-blocking)
  try {
    const snap = await getDoc(doc(getDB(), USERS_COLLECTION, uid));
    if (!snap.exists()) return;
    const profile = snap.data() as UserProfile;

    const newWinStreak = result.isWinner ? profile.winStreak + 1 : 0;
    const newExp = profile.experience + result.expGained;
    const { newLevel, remainingExp } = calculateLevel(profile.level, newExp);

    const updates: Record<string, any> = {
      rating: Math.max(100, profile.rating + result.ratingDelta),
      gamesPlayed: increment(1),
      experience: remainingExp,
      level: newLevel,
      winStreak: newWinStreak,
      longestWinStreak: Math.max(profile.longestWinStreak, newWinStreak),
      lastSeen: serverTimestamp(),
    };

    // Coins пишет сервер. С клиента — только локальная игра с ботом.
    if (!skipCoins) {
      updates.coins = increment(result.coinsGained);
    }

    if (result.isWinner) {
      updates.wins = increment(1);
      if (result.isBackgammon) updates.backgammonsWon = increment(1);
      else if (result.isGammon) updates.gammonsWon = increment(1);
    } else {
      updates.losses = increment(1);
    }

    // Сохраняем новые достижения (merge с существующим массивом)
    if (result.newAchievements && result.newAchievements.length > 0) {
      const currentAchs: string[] = Array.isArray(profile.achievements) ? profile.achievements : [];
      const achSet = new Set<string>([...currentAchs, ...result.newAchievements]);
      updates.achievements = Array.from(achSet);
    }

    await updateDoc(doc(getDB(), USERS_COLLECTION, uid), updates);
  } catch (err) {
    console.warn("Could not sync game result to Firestore:", err);
  }
}

function calculateLevel(
  currentLevel: number,
  totalExp: number
): { newLevel: number; remainingExp: number } {
  let level = currentLevel;
  let exp = totalExp;
  let threshold = expForNextLevel(level);
  while (exp >= threshold) {
    exp -= threshold;
    level += 1;
    threshold = expForNextLevel(level);
  }
  return { newLevel: level, remainingExp: exp };
}

export function expForNextLevel(level: number): number {
  return Math.floor(100 * Math.pow(1.4, level - 1));
}

export async function getLeaderboard(
  limitCount = 50
): Promise<UserProfile[]> {
  try {
    const q = query(
      collection(getDB(), USERS_COLLECTION),
      orderBy("rating", "desc"),
      limit(limitCount)
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data() as UserProfile);
  } catch (_) {
    return [];
  }
}

export function getRankLabel(rating: number): string {
  if (rating < 900) return "Начинающий";
  if (rating < 1100) return "Любитель";
  if (rating < 1300) return "Опытный";
  if (rating < 1500) return "Умелый";
  if (rating < 1700) return "Эксперт";
  if (rating < 2000) return "Мастер";
  return "Грандмастер";
}
